import { useEffect } from 'react'
import { useAppDispatch } from '@/hooks/useAppDispatch'
import { useAppSelector } from '@/hooks/useAppSelector'
import { findTenantBySubdomain } from '@/utils/hostTenant'
import { enterNewTenant, selectCurrentTenantId, selectTenants } from './authSlice'
import { selectIsImpersonating } from './impersonation'
import {
  HOST_TENANT_STATUS,
  hostTenantApplying,
  hostTenantResolved,
  selectHostTenant,
} from './hostTenantSlice'

/**
 * Make the host's workspace current for a freshly verified session.
 *
 * A member is switched in through `enterNewTenant` (switchTenant, so every
 * tenant-scoped slice resets; a failed switch call still lands them there).
 * A non-member keeps the normal behaviour - their current workspace or the
 * picker - and the picker says why. An impersonation pin is left alone.
 */
export const applyHostTenant = () => async (dispatch, getState) => {
  const state = getState()
  const { subdomain, status } = selectHostTenant(state)
  if (status !== HOST_TENANT_STATUS.PENDING) return
  dispatch(hostTenantApplying())

  if (selectIsImpersonating(state)) {
    dispatch(hostTenantResolved(HOST_TENANT_STATUS.APPLIED))
    return
  }

  const hostTenant = findTenantBySubdomain(selectTenants(state), subdomain)
  if (!hostTenant) {
    dispatch(hostTenantResolved(HOST_TENANT_STATUS.NOT_MEMBER))
    return
  }

  if (String(hostTenant.id) !== String(selectCurrentTenantId(state) ?? '')) {
    await dispatch(enterNewTenant(hostTenant))
  }
  dispatch(hostTenantResolved(HOST_TENANT_STATUS.APPLIED))
}

/**
 * Applies the host's workspace once a sign-in is verified (/auth/me on load,
 * or any login path). Mounted once, in App.
 */
export function useHostTenant() {
  const dispatch = useAppDispatch()
  const isSessionReady = useAppSelector(
    (state) => state.auth.isAuthenticated && state.auth.isSessionVerified,
  )
  const { status } = useAppSelector(selectHostTenant)

  useEffect(() => {
    if (isSessionReady && status === HOST_TENANT_STATUS.PENDING) {
      dispatch(applyHostTenant())
    }
  }, [dispatch, isSessionReady, status])
}
